import styled, { keyframes } from "styled-components";
import { theme, typography } from "../styles";

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`;

export const HeroSection = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
  padding: 48px 16px 80px;
  overflow: hidden;
  text-align: center;

  @media (min-width: 640px) {
    padding: 64px 24px 96px;
  }

  @media (min-width: 768px) {
    padding: 80px 24px 128px;
  }

  @media (max-width: 767px) {
    min-height: auto;
  }
`;

export const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background-image: ${theme.gradientHero};
`;

export const HeroBlurOrb = styled.div`
  pointer-events: none;
  position: absolute;
  top: 25%;
  left: 50%;
  width: min(800px, 90vw);
  height: min(800px, 90vw);
  transform: translateX(-50%);
  border-radius: 50%;
  background: hsl(32 95% 55% / 0.05);
  filter: blur(120px);
  animation: ${pulseGlow} 3s ease-in-out infinite;
`;

export const HeroGrid = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image:
    linear-gradient(${theme.foreground} 1px, transparent 1px),
    linear-gradient(90deg, ${theme.foreground} 1px, transparent 1px);
  background-size: 60px 60px;
`;

export const HeroBottomFade = styled.div`
  pointer-events: none;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 128px;
  background: linear-gradient(to top, ${theme.bg}, transparent);
`;

export const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 896px;
  margin: 0 auto;
`;

export const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
  padding: 8px 16px;
  border: 1px solid ${theme.border};
  border-radius: 999px;
  background: ${theme.muted};
  animation: ${fadeUp} 0.8s ease-out forwards;
`;

export const HeroBadgeDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${theme.primary};
  animation: ${pulseDot} 2s ease-in-out infinite;
`;

export const HeroBadgeText = styled.span`
  color: ${theme.textMuted};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.2;
  white-space: nowrap;
`;

export const HeroTitle = styled.h1`
  margin: 0 0 24px;
  color: ${theme.foreground};
  font-size: 2.25rem;
  font-weight: ${typography.weights.extrabold};
  line-height: 1.08;
  letter-spacing: -0.04em;
  animation: ${fadeUp} 0.8s ease-out 0.1s forwards;
  opacity: 0;

  span {
    background-image: ${theme.gradientGold};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4.5rem;
  }
`;

export const HeroLead = styled.p`
  margin: 0 auto 40px;
  max-width: 672px;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.55;
  animation: ${fadeUp} 0.8s ease-out 0.2s forwards;
  opacity: 0;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto 48px;
  animation: ${fadeUp} 0.8s ease-out 0.3s forwards;
  opacity: 0;

  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    max-width: none;
    gap: 16px;
    margin-bottom: 64px;
  }
`;

export const HeroCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0 24px;
  border: none;
  border-radius: ${theme.radiusLg};
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  font-family: inherit;
  font-size: 1rem;
  font-weight: ${typography.weights.bold};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.03);
    box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 3px;
  }

  @media (min-width: 640px) {
    width: auto;
    min-height: 56px;
    padding: 0 40px;
    font-size: 1.125rem;
  }
`;

/** Lovable heroOutline — secondary sample CTA. */
export const HeroOutlineCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0 24px;
  border: 2px solid hsl(var(--hp-primary) / 0.6);
  border-radius: ${theme.radiusLg};
  background: transparent;
  color: ${theme.foreground};
  font-family: inherit;
  font-size: 1rem;
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    background: hsl(var(--hp-primary) / 0.1);
    border-color: hsl(var(--hp-primary));
  }

  &:focus-visible {
    outline: 2px solid ${theme.primary};
    outline-offset: 3px;
  }

  @media (min-width: 640px) {
    width: auto;
    min-height: 56px;
    padding: 0 40px;
    font-size: 1.125rem;
  }
`;

export const HeroSampleScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: hsl(0 0% 0% / 0.8);

  @media (min-width: 640px) {
    padding: 16px;
  }
`;

export const HeroSampleCard = styled.div`
  position: relative;
  width: min(768px, 100%);
  max-height: calc(100vh - 24px);
  overflow: auto;
  border-radius: 12px;
  border: 1px solid ${theme.border};
  background: ${theme.card};
  box-shadow: ${theme.shadowCard};

  @media (min-width: 640px) {
    border-radius: ${theme.radiusLg};
    max-height: none;
    overflow: hidden;
  }
`;

export const HeroSampleVideo = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${theme.bg};

  .wistia_embed {
    width: 100%;
    height: 100%;
    min-height: 0;
    aspect-ratio: 16 / 9;
    background: ${theme.bg};
  }
`;

export const HeroSampleClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: hsl(var(--hp-secondary) / 0.9);
  color: ${theme.foreground};
  cursor: pointer;
  opacity: 0.95;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  .material-icons {
    font-size: 20px;
  }

  @media (min-width: 640px) {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: 4px;

    .material-icons {
      font-size: 18px;
    }
  }
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  max-width: 768px;
  margin: 0 auto;
  animation: ${fadeUp} 0.8s ease-out 0.4s forwards;
  opacity: 0;

  @media (min-width: 640px) {
    gap: 24px;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StatCard = styled.div`
  padding: 8px;
  text-align: center;

  @media (min-width: 640px) {
    padding: 16px;
  }
`;

export const StatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: ${theme.primary};

  .material-icons {
    font-size: 24px;
  }
`;

export const StatLabel = styled.div`
  color: ${theme.foreground};
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
`;

export const StatSublabel = styled.div`
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.35;
`;
